/**
 * Utility to filter out i18next props (t, tReady, i18n) and Redux connect props
 * before passing them to styled-components.
 *
 * These props are injected by react-i18next's withTranslation HOC or Redux connect
 * and should not be forwarded to DOM elements.
 */

// Props to filter out from styled-components
const i18nextProps = new Set(['t', 'tReady', 'i18n']);

// Redux connect dispatch props that should not be forwarded to DOM elements
const reduxConnectProps = new Set([
  'updateSearchQueryParamsConnect',
  'updateSearchQueryStringConnect',
  'resetSortConnect',
  'resetLimitConnect',
  'resetPageConnect',
  'clearAllFiltersConnect',
]);

// Combined set of all props to filter
const propsToFilter = new Set([...i18nextProps, ...reduxConnectProps]);

/**
 * Filters out i18next props and Redux connect props from an object.
 * Use this when spreading props onto a styled-component.
 *
 * @example
 * const MyComponent: React.FC<IProps> = (props) => (
 *   <StyledDiv {...filterI18nProps(props)} />
 * );
 */
export function filterI18nProps<T extends object>(props: T): T {
  const filtered = { ...props } as Record<string, unknown>;
  propsToFilter.forEach((prop) => {
    delete filtered[prop];
  });
  return filtered as T;
}

/**
 * shouldForwardProp function for styled-components.
 * Use this with styled() to prevent i18next and Redux connect props from being forwarded.
 *
 * @example
 * const StyledDiv = styled.div.withConfig({
 *   shouldForwardProp: shouldForwardI18nProp,
 * })`
 *   // styles here
 * `;
 */
export function shouldForwardI18nProp(prop: string): boolean {
  return !propsToFilter.has(prop);
}
